import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { cn, formatDate } from "@/lib/utils";
import type {
  AiSummaryResult,
  ClaudeCostBreakdown,
  Job,
  JobResultCost,
  Meeting,
  MeetingAttachment,
  MeetingMessage,
  MeetingReference,
  ReplaceRule,
  SectionKey,
  ShareLink,
} from "@/types";
import {
  formatTwd,
  formatTokens,
  parseCostFromResultData,
  shortModelName,
} from "@/lib/cost";
import {
  buildGenerateCommand,
  buildRefineCommand,
  getEngineMode,
} from "@/lib/engine";
import { AssistantHandoff } from "@/components/AssistantHandoff";
import { CommandBlock } from "@/components/CommandBlock";

// 列印 PDF：opens browser print dialog (使用者可選「儲存為 PDF」)
function printIframe(iframe: HTMLIFrameElement | null) {
  if (!iframe?.contentWindow) return;
  try {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
  } catch {
    window.print();
  }
}

// 對話視窗一次最多保留的「使用者送出」次數，達上限要清理才能繼續
const CHAT_TURN_LIMIT = 10;

export default function MeetingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [messages, setMessages] = useState<MeetingMessage[]>([]);
  const [references, setReferences] = useState<MeetingReference[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // 浮動對話 panel 開關
  const [chatOpen, setChatOpen] = useState(false);
  // 手機版 bottom sheet：是否摺疊成底部 chip（讓用戶能看見後方 iframe 預覽更新）
  const [chatMinimized, setChatMinimized] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [refining, setRefining] = useState(false);
  // 對話模式：refine = 修改既有整理；qa = 提問並寫進「對話新增」章節
  const [chatMode, setChatMode] = useState<"refine" | "qa">("refine");
  // 標記最近一次 qa 回答的 assistant 訊息 id：對話視窗在該訊息下方顯示「已寫進對話新增」
  const [lastQaAssistantId, setLastQaAssistantId] = useState<number | null>(null);

  // 整理引擎（Claude Code / API），在「新增會議」頁切換，這裡只讀
  const engineMode = getEngineMode();
  // Claude Code 模式下，對話送出時產生的指令（給使用者複製到終端機）
  const [cliRefineCommand, setCliRefineCommand] = useState<string | null>(null);

  // 達到對話上限時跳的清理確認 modal
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [clearingMessages, setClearingMessages] = useState(false);

  // 重新分析狀態（真實進度由 polling job 取得）
  const [regenerating, setRegenerating] = useState(false);
  const [activeJob, setActiveJob] = useState<Job | null>(null);
  const [smoothPercent, setSmoothPercent] = useState(0); // 前端平滑內插
  const regenStartedAtRef = useRef<number>(0);

  // 最近一次 AI 成本（refine 或 regenerate 後顯示）
  const [recentRefineCost, setRecentRefineCost] =
    useState<ClaudeCostBreakdown | null>(null);
  const [recentRegenCost, setRecentRegenCost] =
    useState<JobResultCost | null>(null);

  // 業主資料管理 panel 開關
  const [refsOpen, setRefsOpen] = useState(false);

  // 逐字稿編輯 panel 開關
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  // 修錯字 / 分享 modal
  const [replaceModalOpen, setReplaceModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // iframe ref（用於觸發列印）
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // 是否略過 Claude 的標題建議（記在 sessionStorage 避免每次重新整理都看到）
  const [dismissedSuggestion, setDismissedSuggestion] = useState(false);

  useEffect(() => {
    if (!id) return;
    void loadAll(id);
  }, [id]);

  async function loadAll(meetingId: string) {
    setLoading(true);
    try {
      const [m, msgs, refs] = await Promise.all([
        api.getMeeting(meetingId),
        api.listMessages(meetingId).catch(() => ({ messages: [] })),
        api.listReferences(meetingId).catch(() => ({ references: [] })),
      ]);
      setMeeting(m.meeting);
      setMessages(msgs.messages);
      setReferences(refs.references);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  const summary = useMemo<AiSummaryResult | null>(() => {
    if (!meeting?.ai_summary) return null;
    try {
      return JSON.parse(meeting.ai_summary) as AiSummaryResult;
    } catch {
      return null;
    }
  }, [meeting?.ai_summary]);

  // 已使用的對話次數（user 訊息數）
  const userTurnCount = useMemo(
    () => messages.filter((m) => m.role === "user").length,
    [messages],
  );
  const reachedLimit = userTurnCount >= CHAT_TURN_LIMIT;

  async function sendRefine() {
    if (!id || !chatInput.trim() || refining) return;
    // 達上限：擋住送出，跳清理 modal
    if (reachedLimit) {
      setShowLimitModal(true);
      return;
    }
    const text = chatInput.trim();

    // Claude Code 模式：不打 API，改吐一段指令給使用者貼到終端機。
    // 使用者訊息不在這裡先塞，等 import 回寫時一起寫進 D1，避免對話出現孤兒訊息。
    if (engineMode === "claude-code") {
      setChatInput("");
      setCliRefineCommand(buildRefineCommand(id, text, chatMode));
      return;
    }

    setChatInput("");
    setRefining(true);

    const userMsg: MeetingMessage = {
      id: -Date.now(),
      meeting_id: id,
      role: "user",
      content: text,
      tokens_input: null,
      tokens_output: null,
      model: null,
      created_at: Math.floor(Date.now() / 1000),
    };
    setMessages((prev) => [...prev, userMsg]);

    const sendingMode = chatMode;
    try {
      const refineRes = await api.refine(id, { message: text, mode: sendingMode });
      if (refineRes.cost) setRecentRefineCost(refineRes.cost);
      const [updated, msgs] = await Promise.all([
        api.getMeeting(id),
        api.listMessages(id),
      ]);
      setMeeting(updated.meeting);
      setMessages(msgs.messages);
      // qa 模式：標記剛剛的 assistant 訊息 id，讓對話視窗加上「已寫進對話新增」提示
      if (sendingMode === "qa") {
        const lastAssistant = [...msgs.messages]
          .reverse()
          .find((m) => m.role === "assistant");
        if (lastAssistant) setLastQaAssistantId(lastAssistant.id);
      }
      // 這次送完正好達到上限：自動跳清理引導
      const newUserCount = msgs.messages.filter((m) => m.role === "user").length;
      if (newUserCount >= CHAT_TURN_LIMIT) {
        setShowLimitModal(true);
      }
    } catch (e) {
      setError((e as Error).message);
      setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
    } finally {
      setRefining(false);
    }
  }

  function downloadChatBackup() {
    if (!meeting) return;
    const lines: string[] = [];
    lines.push(`ChillMeet 對話備份`);
    lines.push(`會議：${meeting.title}`);
    if (meeting.client_name) lines.push(`業主：${meeting.client_name}`);
    if (meeting.meeting_date) lines.push(`會議日期：${meeting.meeting_date}`);
    lines.push(`備份時間：${new Date().toLocaleString("zh-TW")}`);
    lines.push(`共 ${messages.length} 則訊息（${userTurnCount} 次對話）`);
    lines.push("");
    lines.push("─".repeat(40));
    lines.push("");
    messages.forEach((m, idx) => {
      const ts = new Date((m.created_at || 0) * 1000).toLocaleString("zh-TW");
      const who = m.role === "user" ? "你" : "ChillMeet";
      lines.push(`[#${idx + 1}] ${who} · ${ts}`);
      lines.push(m.content);
      lines.push("");
    });
    const blob = new Blob([lines.join("\n")], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeTitle = meeting.title.replace(/[\\/:*?"<>|]/g, "_");
    a.download = `ChillMeet_對話備份_${safeTitle}_${new Date()
      .toISOString()
      .slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function clearChat() {
    if (!id || clearingMessages) return;
    setClearingMessages(true);
    try {
      await api.clearMessages(id);
      setMessages([]);
      setShowLimitModal(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setClearingMessages(false);
    }
  }

  async function handleUpdateMeta(patch: {
    title?: string;
    client_name?: string;
    meeting_date?: string;
  }) {
    if (!id) return;
    await api.updateMeeting(id, patch);
    if (meeting) {
      setMeeting({
        ...meeting,
        ...patch,
        client_name: patch.client_name ?? meeting.client_name,
        meeting_date: patch.meeting_date ?? meeting.meeting_date,
      });
    }
  }

  async function handleRegenerate() {
    if (!id || regenerating) return;
    setRegenerating(true);
    setError(null);
    setActiveJob(null);
    setSmoothPercent(0);
    regenStartedAtRef.current = Date.now();

    // 啟動 polling（不依賴 POST 拿到的 job_id；用 active-job endpoint 找）
    let stopPolling = false;
    const pollLoop = async () => {
      while (!stopPolling) {
        try {
          const { job } = await api.getActiveJob(id);
          if (job && job.status !== "cancelled") {
            setActiveJob(job);
            if (job.status === "completed" || job.status === "failed") {
              break;
            }
          }
        } catch {
          // 短暫網路錯誤忽略
        }
        await new Promise((r) => setTimeout(r, 1200));
      }
    };

    // 平滑內插：每 200ms 增 0.4%，最多到當前 server progress
    const smoothId = setInterval(() => {
      setSmoothPercent((cur) => {
        const target = Math.max(activeJob?.progress_percent ?? 0, cur);
        const ceiling = Math.min(target + 8, 95); // 不要超過 server 太多
        if (cur >= ceiling) return cur;
        return Math.min(cur + 0.4, ceiling);
      });
    }, 200);

    const polling = pollLoop();
    try {
      const startRes = await api.startProcessing(id);
      stopPolling = true;
      await polling;
      // 取最終 job 解析成本
      try {
        const { job: finalJob } = await api.getJob(startRes.job_id);
        const cost = parseCostFromResultData(finalJob.result_data);
        if (cost) setRecentRegenCost(cost);
      } catch {
        // 取不到不影響主流程
      }
      await loadAll(id);
    } catch (e) {
      stopPolling = true;
      setError((e as Error).message);
    } finally {
      clearInterval(smoothId);
      setRegenerating(false);
      setActiveJob(null);
      setSmoothPercent(0);
    }
  }

  // 同步 server progress 到 smooth 顯示（避免回退）
  useEffect(() => {
    if (!activeJob) return;
    setSmoothPercent((cur) => Math.max(cur, activeJob.progress_percent));
  }, [activeJob?.progress_percent]);

  async function refreshReferences() {
    if (!id) return;
    const r = await api.listReferences(id);
    setReferences(r.references);
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-12 text-center text-brand-dark/40">
        載入中⋯⋯
      </div>
    );
  }

  if (error && !meeting) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-12 text-center text-red-700">
        {error}
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-12 text-center text-red-700">
        找不到這場會議
      </div>
    );
  }

  const showSuggestionBanner =
    !dismissedSuggestion &&
    summary?.suggested_title &&
    summary.suggested_title.trim() &&
    summary.suggested_title.trim() !== meeting.title.trim();

  return (
    <div className="bg-brand-gray/5 min-h-[calc(100vh-130px)]">
      {/* 上方資訊列：可編輯 metadata。sticky 在頂端，滾動不消失 */}
      <MetaBar
        meeting={meeting}
        onUpdate={handleUpdateMeta}
        summary={summary}
        regenerating={regenerating}
        onPrintPdf={() => printIframe(iframeRef.current)}
        onOpenShare={() => setShareModalOpen(true)}
        onOpenReplace={() => setReplaceModalOpen(true)}
      />

      {/* Claude 建議標題 banner */}
      {showSuggestionBanner && (
        <SuggestionBanner
          summary={summary!}
          currentMeeting={meeting}
          onApply={async (patch) => {
            await handleUpdateMeta(patch);
            setDismissedSuggestion(true);
          }}
          onDismiss={() => setDismissedSuggestion(true)}
        />
      )}

      {/* 業主資料管理（摺疊） */}
      <ReferencesPanel
        meetingId={meeting.id}
        references={references}
        open={refsOpen}
        onToggle={() => setRefsOpen((v) => !v)}
        onChanged={refreshReferences}
        onRegenerate={handleRegenerate}
        regenerating={regenerating}
        canRegenerate={meeting.status !== "processing"}
      />

      {/* 附帶頁面（摺疊）：會前／會後另外整理的網頁 */}
      <AttachmentsPanel
        meetingId={meeting.id}
        attachments={meeting.attachments ?? []}
        onChanged={() => loadAll(meeting.id)}
      />

      {/* 逐字稿編輯（摺疊） */}
      <TranscriptPanel
        meetingId={meeting.id}
        open={transcriptOpen}
        onToggle={() => setTranscriptOpen((v) => !v)}
        onRegenerate={handleRegenerate}
        regenerating={regenerating}
        canRegenerate={meeting.status !== "processing"}
      />

      {/* 主內容：iframe 顯示渲染後的會議紀錄（高度自動撐滿內容，不出現內嵌捲軸） */}
      <div className="max-w-5xl mx-auto px-0 sm:px-6 pb-24">
        {regenerating ? (
          <RegenerateProgress
            job={activeJob}
            smoothPct={smoothPercent}
            startedAt={regenStartedAtRef.current}
          />
        ) : meeting.generated_html ? (
          <AutoHeightIframe
            key={meeting.updated_at}
            srcDoc={meeting.generated_html}
            title="會議紀錄"
            iframeRef={iframeRef}
          />
        ) : meeting.status === "failed" ? (
          <div className="bg-white rounded-lg border border-red-200 p-12 text-center text-red-700">
            <div className="font-medium mb-2">處理失敗</div>
            <div className="text-sm">{meeting.error_message}</div>
            <button
              onClick={handleRegenerate}
              className="mt-4 text-sm px-4 py-2 bg-red-700 text-white rounded-md hover:bg-red-800"
            >
              重試
            </button>
          </div>
        ) : meeting.status === "awaiting_cli" ? (
          <div className="bg-white rounded-lg border border-brand-gray/20 p-5 sm:p-8 min-w-0">
            <div className="text-[11px] font-bold tracking-[0.2em] text-brand-gold uppercase mb-2">
              等 Claude Code
            </div>
            <h2 className="font-serif text-lg sm:text-xl font-bold text-brand-dark mb-2">
              資料都備好了，等終端機那邊整理完
            </h2>
            <AssistantHandoff meetingId={meeting.id} />
            <p className="text-sm text-brand-dark/60 leading-relaxed mb-5">
              或把下面這段貼到 Claude Code，整理完會自動寫回這頁。跑完回來重整就看得到。
            </p>
            <CommandBlock
              command={buildGenerateCommand({
                meetingId: meeting.id,
                title: meeting.title,
                clientName: meeting.client_name,
                meetingDate: meeting.meeting_date,
                sourceType: meeting.source_type,
                plaudUrl: meeting.plaud_url,
                refCount: references.length,
              })}
            />
            <button
              onClick={() => window.location.reload()}
              className="mt-5 text-sm px-4 py-2 rounded-full border border-brand-gray/30 text-brand-dark/70 hover:border-brand-gold"
            >
              跑完了，重新整理
            </button>
          </div>
        ) : (
          <div className="text-center py-32 text-brand-dark/40 text-sm">
            還沒有產出
          </div>
        )}
      </div>

      {/* Claude Code 模式：對話送出後產生的修改指令 */}
      {cliRefineCommand && (
        <div className="fixed inset-0 z-[60] bg-brand-dark/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6 overflow-y-auto">
          <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl p-5 sm:p-8 min-w-0 max-h-[92vh] overflow-y-auto">
            <h2 className="font-serif text-lg sm:text-xl font-bold text-brand-dark mb-2">
              把這段貼到 Claude Code
            </h2>
            <p className="text-sm text-brand-dark/60 leading-relaxed mb-5">
              改完會直接寫回這場會議，對話紀錄也會一起補上。
            </p>
            <CommandBlock command={cliRefineCommand} />
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-6">
              <button
                onClick={() => window.location.reload()}
                className="flex-1 px-5 py-2.5 rounded-full text-sm font-medium bg-brand-dark text-brand-cream hover:bg-brand-gold transition-colors"
              >
                跑完了，重新整理
              </button>
              <button
                onClick={() => setCliRefineCommand(null)}
                className="px-5 py-2.5 rounded-full text-sm text-brand-dark/60 hover:text-brand-dark border border-brand-gray/30"
              >
                關閉
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-red-700 text-white text-xs px-4 py-2 rounded-md shadow-lg z-50">
          {error}
        </div>
      )}

      {/* 最近一次 AI 成本 toast（refine / regenerate 完成後出現，可關閉） */}
      <RecentCostToast
        refine={recentRefineCost}
        regen={recentRegenCost}
        onDismiss={() => {
          setRecentRefineCost(null);
          setRecentRegenCost(null);
        }}
      />

      {/* 右側目錄（TOC）— xl 螢幕才顯示 */}
      <RightSideToc
        iframeRef={iframeRef}
        enabled={!!meeting.generated_html && !regenerating}
      />

      {/* 回到頂端浮動按鈕 */}
      <BackToTop />

      {/* 浮動微調 FAB + Panel
          手機 bottom sheet 行為：
          - 未開啟對話 → FAB 顯示
          - 對話展開（panel 滿版） → FAB 在手機上隱藏（避免擋到 panel 內按鈕）
          - 對話最小化（chip 收在底部） → FAB 維持可見，但只露出右下角，與底部 chip 並存 */}
      <ChatFab
        open={chatOpen}
        onToggle={() => {
          setChatOpen((v) => !v);
          setChatMinimized(false);
        }}
        showHint={!chatOpen && !!meeting.generated_html && messages.length === 0}
        hideOnMobileWhenExpanded={chatOpen && !chatMinimized}
      />
      {chatOpen && (
        <ChatPanel
          messages={messages}
          input={chatInput}
          setInput={setChatInput}
          onSend={sendRefine}
          refining={refining}
          enabled={!!meeting.generated_html}
          onClose={() => {
            setChatOpen(false);
            setChatMinimized(false);
          }}
          turnCount={userTurnCount}
          turnLimit={CHAT_TURN_LIMIT}
          onAskClear={() => setShowLimitModal(true)}
          minimized={chatMinimized}
          onMinimize={() => setChatMinimized(true)}
          onExpand={() => setChatMinimized(false)}
          mode={chatMode}
          setMode={setChatMode}
          lastQaAssistantId={lastQaAssistantId}
        />
      )}

      {/* 達上限：清理確認 modal */}
      {showLimitModal && (
        <ChatLimitModal
          turnCount={userTurnCount}
          turnLimit={CHAT_TURN_LIMIT}
          messageCount={messages.length}
          onBackup={downloadChatBackup}
          onClear={clearChat}
          onClose={() => setShowLimitModal(false)}
          clearing={clearingMessages}
        />
      )}

      {/* 修錯字 modal */}
      {replaceModalOpen && (
        <ReplaceModal
          meetingId={meeting.id}
          onClose={() => setReplaceModalOpen(false)}
          onDone={async () => {
            await loadAll(meeting.id);
            setReplaceModalOpen(false);
          }}
        />
      )}

      {/* 分享 modal */}
      {shareModalOpen && (
        <ShareModal
          meetingId={meeting.id}
          onClose={() => setShareModalOpen(false)}
        />
      )}
    </div>
  );
}

// ===== 上方 Meta Bar：可編輯 title / client / date + 複製選單 =====

function MetaBar({
  meeting,
  onUpdate,
  summary,
  regenerating,
  onPrintPdf,
  onOpenShare,
  onOpenReplace,
}: {
  meeting: Meeting;
  onUpdate: (patch: {
    title?: string;
    client_name?: string;
    meeting_date?: string;
  }) => Promise<void>;
  summary: AiSummaryResult | null;
  regenerating: boolean;
  onPrintPdf: () => void;
  onOpenShare: () => void;
  onOpenReplace: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(meeting.title);
  const [clientName, setClientName] = useState(meeting.client_name ?? "");
  const [meetingDate, setMeetingDate] = useState(meeting.meeting_date ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTitle(meeting.title);
    setClientName(meeting.client_name ?? "");
    setMeetingDate(meeting.meeting_date ?? "");
  }, [meeting.title, meeting.client_name, meeting.meeting_date]);

  async function save() {
    setSaving(true);
    try {
      await onUpdate({
        title: title.trim() || meeting.title,
        client_name: clientName.trim(),
        meeting_date: meetingDate || "",
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setTitle(meeting.title);
    setClientName(meeting.client_name ?? "");
    setMeetingDate(meeting.meeting_date ?? "");
    setEditing(false);
  }

  return (
    <div className="border-b border-brand-gray/15 bg-white/95 backdrop-blur sticky top-0 z-30 shadow-sm">
      <div className="max-w-5xl mx-auto px-6 py-3 flex items-start justify-between gap-4 flex-wrap">
        {/* 手機：標題獨佔一列，按鈕換到下一列（長標題原本會被按鈕蓋住） */}
        <div className="flex-1 min-w-0 basis-full sm:basis-0">
          {editing ? (
            <div className="space-y-2">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="會議主題"
                className="w-full text-lg font-bold font-serif text-brand-dark px-2 py-1 border border-brand-gray/30 rounded-md focus:outline-none focus:border-brand-gold bg-white"
              />
              <div className="flex gap-2 flex-wrap">
                <input
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="業主"
                  className="text-xs px-2 py-1 border border-brand-gray/30 rounded-md focus:outline-none focus:border-brand-gold bg-white"
                />
                <input
                  type="date"
                  value={meetingDate}
                  onChange={(e) => setMeetingDate(e.target.value)}
                  className="text-xs px-2 py-1 border border-brand-gray/30 rounded-md focus:outline-none focus:border-brand-gold bg-white num"
                />
                <button
                  onClick={save}
                  disabled={saving}
                  className="text-xs px-3 py-1 bg-brand-dark text-brand-cream rounded-md hover:bg-brand-gold disabled:opacity-60"
                >
                  {saving ? "儲存中⋯" : "儲存"}
                </button>
                <button
                  onClick={cancel}
                  disabled={saving}
                  className="text-xs px-3 py-1 text-brand-dark/60 hover:text-brand-dark"
                >
                  取消
                </button>
              </div>
            </div>
          ) : (
            <div className="group">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-serif text-lg font-bold text-brand-dark">
                  {meeting.title}
                </h1>
                <button
                  onClick={() => setEditing(true)}
                  className="text-[11px] text-brand-dark/40 hover:text-brand-gold inline-flex items-center gap-1 px-1.5 py-0.5 rounded opacity-60 group-hover:opacity-100 transition-opacity"
                  title="編輯會議資訊"
                >
                  <svg
                    className="w-3 h-3"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4z" />
                  </svg>
                  編輯
                </button>
              </div>
              <div className="text-xs text-brand-dark/55 mt-1 flex items-center gap-2 flex-wrap">
                {meeting.client_name ? (
                  <span className="nb">{meeting.client_name}</span>
                ) : (
                  <span className="italic text-brand-dark/30">未填業主</span>
                )}
                <span className="text-brand-gray">·</span>
                <span className="num">
                  {meeting.meeting_date ?? formatDate(meeting.created_at)}
                </span>
                <span className="text-brand-gray">·</span>
                <StatusLabel status={meeting.status} />
              </div>
            </div>
          )}
        </div>

        {!editing && (
          <div className="flex items-center gap-2 flex-wrap">
            <ToolBtn
              label="修錯字"
              title="批次替換錯字（全文一起改）"
              onClick={onOpenReplace}
              disabled={!summary || regenerating}
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              }
            />
            <ToolBtn
              label="下載 PDF"
              title="開啟列印對話框，可選「儲存為 PDF」"
              onClick={onPrintPdf}
              disabled={!summary || regenerating}
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              }
            />
            <ToolBtn
              label="分享"
              title="建立短網址給業主"
              onClick={onOpenShare}
              disabled={!summary || regenerating}
              variant="gold"
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}

function ToolBtn({
  label,
  title,
  onClick,
  disabled,
  icon,
  variant = "ghost",
}: {
  label: string;
  title: string;
  onClick: () => void;
  disabled: boolean;
  icon: React.ReactNode;
  variant?: "ghost" | "gold";
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        "inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full border transition-colors",
        disabled
          ? "border-brand-gray/30 text-brand-gray cursor-not-allowed"
          : variant === "gold"
            ? "border-brand-gold bg-brand-gold text-white hover:bg-brand-dark hover:border-brand-dark"
            : "border-brand-gray/40 text-brand-dark hover:border-brand-dark hover:bg-brand-cream",
      )}
    >
      <span className="w-3.5 h-3.5">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

// ===== Claude 建議標題 banner =====

function SuggestionBanner({
  summary,
  currentMeeting,
  onApply,
  onDismiss,
}: {
  summary: AiSummaryResult;
  currentMeeting: Meeting;
  onApply: (patch: {
    title?: string;
    client_name?: string;
    meeting_date?: string;
  }) => Promise<void>;
  onDismiss: () => void;
}) {
  const [applying, setApplying] = useState(false);

  const patch: {
    title?: string;
    client_name?: string;
    meeting_date?: string;
  } = {};
  if (
    summary.suggested_title &&
    summary.suggested_title.trim() !== currentMeeting.title.trim()
  ) {
    patch.title = summary.suggested_title.trim();
  }
  if (!currentMeeting.client_name && summary.suggested_client?.trim()) {
    patch.client_name = summary.suggested_client.trim();
  }
  if (!currentMeeting.meeting_date && summary.suggested_date?.trim()) {
    patch.meeting_date = summary.suggested_date.trim();
  }
  const hasChanges = Object.keys(patch).length > 0;
  if (!hasChanges) return null;

  return (
    <div className="border-b border-brand-gold/30 bg-brand-gold/10">
      <div className="max-w-5xl mx-auto px-6 py-3 flex items-start gap-3 flex-wrap">
        <svg
          className="w-4 h-4 mt-0.5 text-brand-gold flex-shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
          <circle cx="12" cy="12" r="4" />
        </svg>
        <div className="flex-1 min-w-0 text-xs text-brand-dark leading-relaxed">
          <div className="font-medium mb-1">Claude 看完內容後，建議：</div>
          <ul className="space-y-0.5 text-brand-dark/80">
            {patch.title && (
              <li>
                會議主題改成「
                <span className="nb font-medium">{patch.title}</span>」
              </li>
            )}
            {patch.client_name && (
              <li>
                業主補上「
                <span className="nb font-medium">{patch.client_name}</span>」
              </li>
            )}
            {patch.meeting_date && (
              <li>
                會議日期補上「
                <span className="num font-medium">{patch.meeting_date}</span>」
              </li>
            )}
          </ul>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={async () => {
              setApplying(true);
              try {
                await onApply(patch);
              } finally {
                setApplying(false);
              }
            }}
            disabled={applying}
            className="text-xs px-3 py-1.5 bg-brand-dark text-brand-cream rounded-md hover:bg-brand-gold disabled:opacity-60"
          >
            {applying ? "套用中⋯" : "全部套用"}
          </button>
          <button
            onClick={onDismiss}
            className="text-xs px-3 py-1.5 text-brand-dark/60 hover:text-brand-dark"
          >
            略過
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== 業主資料管理 panel =====

function ReferencesPanel({
  meetingId,
  references,
  open,
  onToggle,
  onChanged,
  onRegenerate,
  regenerating,
  canRegenerate,
}: {
  meetingId: string;
  references: MeetingReference[];
  open: boolean;
  onToggle: () => void;
  onChanged: () => void | Promise<void>;
  onRegenerate: () => void | Promise<void>;
  regenerating: boolean;
  canRegenerate: boolean;
}) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [adding, setAdding] = useState<null | "web" | "text" | "file">(null);
  const [hasUnsavedChange, setHasUnsavedChange] = useState(false);

  useEffect(() => {
    setHasUnsavedChange(false);
  }, [regenerating]);

  function markDirty() {
    setHasUnsavedChange(true);
  }

  return (
    <div className="border-b border-brand-gray/15 bg-white/80 backdrop-blur">
      <div className="max-w-5xl mx-auto px-6">
        <button
          onClick={onToggle}
          className="w-full py-2.5 flex items-center justify-between gap-3 text-left text-sm text-brand-dark/70 hover:text-brand-dark transition-colors"
        >
          <span className="flex items-center gap-2 min-w-0 flex-wrap">
            <svg
              className={cn(
                "w-3.5 h-3.5 transition-transform",
                open && "rotate-90",
              )}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
            <span className="font-medium">業主提供的參考資料</span>
            <span className="text-xs text-brand-dark/45 num">
              （{references.length} 筆）
            </span>
          </span>
          <span
            onClick={(e) => {
              e.stopPropagation();
              if (!hasUnsavedChange || !canRegenerate || regenerating) return;
              void onRegenerate();
            }}
            title={
              regenerating
                ? "正在重新整理中"
                : !canRegenerate
                  ? "目前狀態無法重新整理"
                  : !hasUnsavedChange
                    ? "沒有新增的資料可重新分析"
                    : "依新資料重新整理"
            }
            className={cn(
              "text-xs px-3 py-1 rounded-full transition-colors flex items-center gap-1.5 shrink-0 whitespace-nowrap",
              hasUnsavedChange && canRegenerate && !regenerating
                ? "bg-brand-gold text-white hover:bg-brand-gold/90 cursor-pointer animate-pulse"
                : "bg-brand-gray/20 text-brand-dark/40 cursor-not-allowed",
            )}
          >
            <svg className={cn("w-3 h-3", regenerating && "animate-spin")} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a9 9 0 1 1-3-6.7" />
              <polyline points="21 4 21 10 15 10" />
            </svg>
            {regenerating
              ? "重新整理中⋯"
              : hasUnsavedChange
                ? "發現新資料，重新分析"
                : "目前已是最新"}
          </span>
        </button>

        {open && (
          <div className="pb-4 space-y-2">
            {references.length === 0 && !adding && (
              <div className="text-xs text-brand-dark/40 italic py-3">
                還沒有參考資料。加上業主分享的網址、檔案、圖片，Claude
                重新整理時會把它們納入分析。
              </div>
            )}

            {references.map((r) => (
              <ReferenceCard
                key={r.id}
                meetingId={meetingId}
                reference={r}
                editing={editingId === r.id}
                onEdit={() => setEditingId(r.id)}
                onCancelEdit={() => setEditingId(null)}
                onChanged={async () => {
                  markDirty();
                  setEditingId(null);
                  await onChanged();
                }}
              />
            ))}

            {adding && (
              <AddReferenceForm
                meetingId={meetingId}
                kind={adding}
                onCancel={() => setAdding(null)}
                onAdded={async () => {
                  markDirty();
                  setAdding(null);
                  await onChanged();
                }}
              />
            )}

            {!adding && (
              <div className="flex gap-2 flex-wrap pt-2">
                <button
                  onClick={() => setAdding("web")}
                  className="text-xs px-3 py-1.5 border border-dashed border-brand-gray/40 rounded-md hover:bg-brand-cream text-brand-dark/60"
                >
                  + 加網頁連結
                </button>
                <button
                  onClick={() => setAdding("text")}
                  className="text-xs px-3 py-1.5 border border-dashed border-brand-gray/40 rounded-md hover:bg-brand-cream text-brand-dark/60"
                >
                  + 加文字
                </button>
                <button
                  onClick={() => setAdding("file")}
                  className="text-xs px-3 py-1.5 border border-dashed border-brand-gray/40 rounded-md hover:bg-brand-cream text-brand-dark/60"
                >
                  + 加檔案 / 圖片
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ReferenceCard({
  meetingId,
  reference,
  editing,
  onEdit,
  onCancelEdit,
  onChanged,
}: {
  meetingId: string;
  reference: MeetingReference;
  editing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onChanged: () => void | Promise<void>;
}) {
  const inlineContent = useMemo<string>(() => {
    if (!reference.metadata) return "";
    try {
      const m = JSON.parse(reference.metadata) as { inline_content?: string };
      return m.inline_content ?? "";
    } catch {
      return "";
    }
  }, [reference.metadata]);

  const [title, setTitle] = useState(reference.title ?? "");
  const [description, setDescription] = useState(reference.description ?? "");
  const [url, setUrl] = useState(reference.url ?? "");
  const [content, setContent] = useState(inlineContent);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setTitle(reference.title ?? "");
    setDescription(reference.description ?? "");
    setUrl(reference.url ?? "");
    setContent(inlineContent);
  }, [reference, inlineContent]);

  async function save() {
    setSaving(true);
    try {
      await api.updateReference(meetingId, reference.id, {
        title,
        description,
        url: reference.ref_type === "web" ? url : undefined,
        content: reference.ref_type === "text" ? content : undefined,
      });
      await onChanged();
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!confirm(`要移除「${reference.title ?? "（未命名）"}」嗎？`)) return;
    setDeleting(true);
    try {
      await api.deleteReference(meetingId, reference.id);
      await onChanged();
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="border border-brand-gray/20 rounded-md p-3 bg-brand-cream/30">
      <div className="flex justify-between items-start mb-2">
        <span className="text-[10px] font-bold text-brand-gold uppercase tracking-[0.15em]">
          {reference.ref_type}
        </span>
        <div className="flex gap-2">
          {!editing && (
            <button
              onClick={onEdit}
              className="text-xs text-brand-dark/50 hover:text-brand-gold"
            >
              編輯
            </button>
          )}
          <button
            onClick={remove}
            disabled={deleting}
            className="text-xs text-brand-dark/50 hover:text-red-700 disabled:opacity-50"
          >
            {deleting ? "刪除中⋯" : "刪除"}
          </button>
        </div>
      </div>

      {editing ? (
        <div className="space-y-2">
          <input
            type="text"
            placeholder="標題"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full text-sm px-2 py-1 border border-brand-gray/30 rounded-md focus:outline-none focus:border-brand-gold bg-white"
          />
          <input
            type="text"
            placeholder="一兩句說明"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full text-xs px-2 py-1 border border-brand-gray/30 rounded-md focus:outline-none focus:border-brand-gold bg-white"
          />
          {reference.ref_type === "web" && (
            <input
              type="url"
              placeholder="https://..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full text-xs px-2 py-1 border border-brand-gray/30 rounded-md focus:outline-none focus:border-brand-gold bg-white"
            />
          )}
          {reference.ref_type === "text" && (
            <textarea
              rows={4}
              placeholder="貼上內容文字"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full text-xs px-2 py-1 border border-brand-gray/30 rounded-md focus:outline-none focus:border-brand-gold bg-white"
            />
          )}
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={saving}
              className="text-xs px-3 py-1 bg-brand-dark text-brand-cream rounded-md hover:bg-brand-gold disabled:opacity-60"
            >
              {saving ? "儲存中⋯" : "儲存"}
            </button>
            <button
              onClick={onCancelEdit}
              disabled={saving}
              className="text-xs px-3 py-1 text-brand-dark/60 hover:text-brand-dark"
            >
              取消
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="text-sm font-medium text-brand-dark">
            {reference.title ?? "（未命名）"}
          </div>
          {reference.description && (
            <div className="text-xs text-brand-dark/55 mt-1 leading-relaxed">
              {reference.description}
            </div>
          )}
          {reference.url && (
            <a
              href={reference.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-brand-gold hover:underline mt-1 inline-block break-all"
            >
              {reference.url}
            </a>
          )}
          {inlineContent && reference.ref_type === "text" && (
            <div className="text-xs text-brand-dark/55 mt-1 leading-relaxed line-clamp-3">
              {inlineContent}
            </div>
          )}
          {reference.file_key && (
            <div className="text-xs text-brand-dark/55 mt-1 num">
              {reference.mime_type ?? "檔案"}
              {reference.file_size
                ? `${(reference.file_size / 1024 / 1024).toFixed(2)} MB`
                : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AddReferenceForm({
  meetingId,
  kind,
  onCancel,
  onAdded,
}: {
  meetingId: string;
  kind: "web" | "text" | "file";
  onCancel: () => void;
  onAdded: () => void | Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [content, setContent] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // PDF 自動處理狀態（壓縮 + 抽文字）
  const [processingPdf, setProcessingPdf] = useState(false);
  const [pdfInfo, setPdfInfo] = useState<{
    phase: string;
    pageDone?: number;
    totalPages?: number;
    attempt?: number;
    totalAttempts?: number;
    beforeMB?: number;
    afterMB?: number;
    textChars?: number;
    hasText?: boolean;
    skipped?: boolean;
  } | null>(null);
  const [extractedText, setExtractedText] = useState<string>("");

  async function handleFilePick(picked: File | null) {
    setError(null);
    setPdfInfo(null);
    setExtractedText("");
    if (!picked) {
      setFile(null);
      return;
    }
    // 非 PDF 直接設定，不處理
    if (picked.type !== "application/pdf") {
      setFile(picked);
      return;
    }

    // PDF：並行壓縮（如果 > 2MB） + 抽文字
    setProcessingPdf(true);
    try {
      const lib = await import("@/lib/compressPdf");

      // 1) 並行：抽文字 + 壓縮
      setPdfInfo({ phase: "extracting" });
      const needCompress = picked.size > 2 * 1024 * 1024;

      const [textResult, compressResult] = await Promise.all([
        lib.extractTextFromPdf(picked),
        needCompress
          ? lib.compressPdf(picked, {
              targetMB: 2,
              onProgress: (info) =>
                setPdfInfo((prev) => ({
                  ...(prev ?? { phase: "" }),
                  phase: "rendering",
                  pageDone: info.pageDone,
                  totalPages: info.totalPages,
                  attempt: info.attempt,
                  totalAttempts: info.totalAttempts,
                })),
            })
          : Promise.resolve({
              file: picked,
              beforeBytes: picked.size,
              afterBytes: picked.size,
              pages: 0,
              attemptUsed: null,
              skipped: true,
            }),
      ]);

      setFile(compressResult.file);
      setExtractedText(textResult.text);
      setPdfInfo({
        phase: "done",
        beforeMB: compressResult.beforeBytes / 1024 / 1024,
        afterMB: compressResult.afterBytes / 1024 / 1024,
        textChars: textResult.text.length,
        hasText: textResult.hasText,
        skipped: compressResult.skipped,
        totalPages: textResult.pages,
      });

      if (!textResult.hasText) {
        setError(
          "這份 PDF 沒有內嵌文字（可能是設計稿或掃描檔）。AI 會在處理時自動改用視覺辨識讀取（每頁多耗 ~2K tokens、稍慢），仍可直接按「加入」上傳。",
        );
      }
    } catch (e) {
      setError(`PDF 處理失敗：${(e as Error).message}，將以原檔上傳`);
      setFile(picked);
    } finally {
      setProcessingPdf(false);
    }
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      if (kind === "web") {
        if (!url.trim()) {
          setError("請填網址");
          return;
        }
        await api.addReference(meetingId, {
          ref_type: "web",
          title: title.trim() || undefined,
          description: description.trim() || undefined,
          url: url.trim(),
        });
      } else if (kind === "text") {
        if (!content.trim()) {
          setError("請填內容");
          return;
        }
        await fetch(`/api/meetings/${meetingId}/references`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ref_type: "text",
            title: title.trim() || "純文字",
            description: description.trim(),
            content: content.trim(),
          }),
        });
      } else if (kind === "file") {
        if (!file) {
          setError("請選檔案");
          return;
        }
        const form = new FormData();
        form.append("file", file);
        const refType = file.type.startsWith("image/")
          ? "image"
          : file.type === "application/pdf"
            ? "pdf"
            : "file";
        form.append("ref_type", refType);
        form.append("title", title.trim() || file.name);
        form.append("description", description.trim());
        // PDF 抽出來的文字一起帶上，後端會存進 metadata.inline_content
        // Claude 看文字而非 PDF base64，避開 30s 超時
        if (refType === "pdf" && extractedText) {
          form.append("extracted_text", extractedText);
        }
        const res = await fetch(`/api/meetings/${meetingId}/references`, {
          method: "POST",
          body: form,
        });
        if (!res.ok) {
          const e = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(e.error ?? "上傳失敗");
        }
      }
      await onAdded();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="border-2 border-dashed border-brand-gold/40 rounded-md p-3 bg-brand-cream/30 space-y-2">
      <div className="text-[10px] font-bold text-brand-gold uppercase tracking-[0.15em]">
        新增{kind === "web" ? "網頁" : kind === "text" ? "文字" : "檔案"}
      </div>
      <input
        type="text"
        placeholder="標題"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full text-sm px-2 py-1 border border-brand-gray/30 rounded-md focus:outline-none focus:border-brand-gold bg-white"
      />
      <input
        type="text"
        placeholder="一兩句說明（選填）"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        className="w-full text-xs px-2 py-1 border border-brand-gray/30 rounded-md focus:outline-none focus:border-brand-gold bg-white"
      />
      {kind === "web" && (
        <input
          type="url"
          placeholder="https://..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="w-full text-xs px-2 py-1 border border-brand-gray/30 rounded-md focus:outline-none focus:border-brand-gold bg-white"
        />
      )}
      {kind === "text" && (
        <textarea
          rows={4}
          placeholder="貼上內容文字"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="w-full text-xs px-2 py-1 border border-brand-gray/30 rounded-md focus:outline-none focus:border-brand-gold bg-white"
        />
      )}
      {kind === "file" && (
        <>
          <input
            type="file"
            onChange={(e) => void handleFilePick(e.target.files?.[0] ?? null)}
            disabled={processingPdf}
            className="w-full text-xs disabled:opacity-50"
          />
          {processingPdf && pdfInfo && (
            <div className="text-[11px] text-brand-dark/70 bg-brand-gold/10 border border-brand-gold/30 rounded-md p-2 leading-relaxed">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
                <span>
                  {pdfInfo.phase === "extracting" && "抽取 PDF 文字 + 壓縮中⋯"}
                  {pdfInfo.phase === "loading-libs" && "載入處理工具⋯"}
                  {pdfInfo.phase === "rendering" &&
                    `壓縮中（嘗試 ${pdfInfo.attempt}/${pdfInfo.totalAttempts}）`}
                  {pdfInfo.pageDone !== undefined && pdfInfo.totalPages !== undefined && (
                    <span className="num">
                      　{pdfInfo.pageDone}/{pdfInfo.totalPages} 頁
                    </span>
                  )}
                </span>
              </div>
            </div>
          )}
          {!processingPdf && pdfInfo?.phase === "done" && (
            <div className="text-[11px] bg-brand-green/10 border border-brand-green/30 rounded-md p-2 leading-relaxed space-y-1">
              <div className="text-brand-green flex items-center gap-1.5">
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span className="font-medium">PDF 處理完成</span>
              </div>
              <div className="text-brand-dark/65 num">
                {!pdfInfo.skipped && pdfInfo.beforeMB !== undefined && (
                  <>檔案 {pdfInfo.beforeMB.toFixed(2)} MB → {pdfInfo.afterMB?.toFixed(2)} MB · </>
                )}
                {pdfInfo.totalPages} 頁 · 抽取 {pdfInfo.textChars?.toLocaleString()} 字
              </div>
              <div className="text-brand-dark/45 text-[10px]">
                {pdfInfo.hasText
                  ? "AI 會看抽出的文字進行分析（不送 PDF 圖檔，速度快、token 少）"
                  : "AI 會在處理時用視覺辨識讀取 PDF（每頁多耗 ~2K tokens、可正常讀取設計稿/掃描檔）"}
              </div>
            </div>
          )}
        </>
      )}
      {error && (
        <div className="text-xs text-red-700">{error}</div>
      )}
      <div className="flex gap-2">
        <button
          onClick={save}
          disabled={saving}
          className="text-xs px-3 py-1 bg-brand-dark text-brand-cream rounded-md hover:bg-brand-gold disabled:opacity-60"
        >
          {saving ? "儲存中⋯" : "加入"}
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          className="text-xs px-3 py-1 text-brand-dark/60 hover:text-brand-dark"
        >
          取消
        </button>
      </div>
    </div>
  );
}

// ===== 浮動對話 FAB + Panel =====

function ChatFab({
  open,
  onToggle,
  showHint,
  hideOnMobileWhenExpanded = false,
}: {
  open: boolean;
  onToggle: () => void;
  showHint: boolean;
  // 手機展開全屏 panel 時隱藏 FAB（避免擋住 panel 內部按鈕），桌機保持顯示
  hideOnMobileWhenExpanded?: boolean;
}) {
  return (
    <div
      className={cn(
        "fixed bottom-6 right-6 z-40 items-center gap-3",
        hideOnMobileWhenExpanded ? "hidden sm:flex" : "flex",
      )}
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {/* 提示氣泡：未開啟、且尚未對話過時顯示，提醒用戶可用對話更新內容 */}
      {!open && showHint && (
        <div className="hidden sm:flex items-start gap-2 bg-white border border-brand-gold/30 px-3 py-2 rounded-lg shadow-md text-xs text-brand-dark/80 max-w-[240px] leading-relaxed animate-fade-in">
          <svg
            className="w-3.5 h-3.5 mt-0.5 text-brand-gold flex-shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.8.8 1 1.6 1 2.3v1h6v-1c0-.7.2-1.5 1-2.3A7 7 0 0 0 12 2z" />
          </svg>
          <span>
            想改內容或補資訊？點這裡跟
            <span className="font-medium text-brand-dark">即時微調</span>
            說一聲，內容會即時更新。
          </span>
        </div>
      )}
      <button
        onClick={onToggle}
        aria-label={open ? "關閉即時微調" : "開啟即時微調"}
        title={open ? "關閉即時微調" : "即時微調：用對話更新內容"}
        className={cn(
          "relative w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all",
          open
            ? "bg-brand-dark text-brand-cream"
            : "bg-brand-gold text-white hover:bg-brand-dark hover:text-brand-cream",
        )}
      >
        {open ? (
          <svg
            className="w-6 h-6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        ) : (
          <svg
            className="w-6 h-6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        )}
      </button>
    </div>
  );
}

function ChatPanel({
  messages,
  input,
  setInput,
  onSend,
  refining,
  enabled,
  onClose,
  turnCount,
  turnLimit,
  onAskClear,
  minimized,
  onMinimize,
  onExpand,
  mode,
  setMode,
  lastQaAssistantId,
}: {
  messages: MeetingMessage[];
  input: string;
  setInput: (v: string) => void;
  onSend: () => void;
  refining: boolean;
  enabled: boolean;
  onClose: () => void;
  turnCount: number;
  turnLimit: number;
  onAskClear: () => void;
  // 手機 bottom sheet：是否摺疊成底部 chip（讓用戶能看到後方 iframe 預覽）
  minimized: boolean;
  onMinimize: () => void;
  onExpand: () => void;
  // 對話模式：refine = 修改既有整理；qa = 提問並寫進「對話新增」章節
  mode: "refine" | "qa";
  setMode: (m: "refine" | "qa") => void;
  // 最近一次 qa 回答的 assistant 訊息 id：在該訊息下方加「已寫進對話新增」提示
  lastQaAssistantId: number | null;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollRef.current && !minimized) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, refining, minimized]);

  const reachedLimit = turnCount >= turnLimit;

  // ===== 手機 bottom sheet 拖收邏輯 =====
  // 手機 expanded：往下拖 ≥ 80px → onMinimize
  // 手機 collapsed：往上拖 ≥ 80px → onExpand
  // 桌機（sm 以上）：sm:translate-y-0 + 不觸發拖動，維持小浮窗行為
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartY = useRef(0);

  const startDrag = (clientY: number) => {
    if (typeof window !== "undefined" && window.innerWidth >= 640) return;
    dragStartY.current = clientY;
    setDragging(true);
    setDragOffset(0);
  };
  const moveDrag = (clientY: number) => {
    if (!dragging) return;
    let dy = clientY - dragStartY.current;
    if (minimized) {
      if (dy > 0) dy = 0; // chip 只允許往上拖
    } else if (dy < 0) {
      dy = 0; // expanded 只允許往下拖
    }
    setDragOffset(dy);
  };
  const endDrag = () => {
    if (!dragging) return;
    if (minimized && dragOffset < -80) {
      onExpand();
    } else if (!minimized && dragOffset > 80) {
      onMinimize();
    }
    setDragging(false);
    setDragOffset(0);
  };

  // 拖動中的 inline transform（僅手機生效；桌機 sm:translate-y-0 會覆蓋此值）
  const dragStyle: React.CSSProperties = dragging
    ? {
        transform: minimized
          ? `translateY(calc(100% - 56px + ${dragOffset}px))`
          : `translateY(${dragOffset}px)`,
      }
    : {};

  // chip 顯示用：最近一條助理訊息（縮短）
  const lastAssistantMsg = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === "assistant") {
        return messages[i].content.replace(/\s+/g, " ").slice(0, 22);
      }
    }
    return null;
  }, [messages]);

  return (
    <div
      className={cn(
        "fixed z-30 bg-white border border-brand-gray/20 shadow-2xl flex flex-col",
        // 手機：bottom sheet 滿版（從 navbar 下方算到底）
        "left-0 right-0 bottom-0 top-14 rounded-t-2xl",
        // 桌機：小浮窗（覆蓋手機的 inset 設定）
        "sm:left-auto sm:right-6 sm:bottom-24 sm:top-auto sm:w-[400px] sm:max-h-[640px] sm:rounded-lg",
        // 手機 collapsed：transform 推到底部只剩 chip header；桌機強制 translate-y-0
        minimized && !dragging
          ? "translate-y-[calc(100%-56px)] sm:translate-y-0"
          : "translate-y-0",
        // 拖動時關閉 transition；非拖動時手機才開（桌機本來就不用 transform）
        dragging
          ? ""
          : "transition-transform duration-300 ease-out sm:transition-none",
      )}
      style={dragStyle}
    >
      {/* Header / Drag handle 區：手機可拖、可點擊切換 expand/minimize；桌機只是視覺標題列 */}
      <div
        className={cn(
          "px-4 pt-2 pb-3 sm:py-3 border-b border-brand-gray/15 bg-brand-cream/40 rounded-t-2xl sm:rounded-t-lg flex-shrink-0 select-none touch-pan-y",
          minimized
            ? "cursor-pointer"
            : "cursor-grab active:cursor-grabbing sm:cursor-default",
        )}
        onTouchStart={(e) => startDrag(e.touches[0].clientY)}
        onTouchMove={(e) => moveDrag(e.touches[0].clientY)}
        onTouchEnd={endDrag}
        onTouchCancel={endDrag}
        onClick={() => {
          if (minimized) onExpand();
        }}
      >
        {/* 拖把手 — 只手機顯示 */}
        <div className="sm:hidden flex justify-center mb-1.5">
          <div className="w-10 h-1 rounded-full bg-brand-gray/30" />
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-brand-dark flex items-center gap-2">
              <span>即時微調</span>
              {minimized && lastAssistantMsg && (
                <span className="text-[10px] text-brand-dark/45 truncate sm:hidden">
                  · {lastAssistantMsg}⋯
                </span>
              )}
            </div>
            <div className="text-[10px] text-brand-dark/55 leading-relaxed truncate">
              {minimized
                ? "點擊或上拉展開繼續對話"
                : mode === "qa"
                  ? "問題與答案會寫進左邊「對話新增」章節"
                  : "改完內容會即時更新到左邊"}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span
              className={cn(
                "text-[10px] num px-2 py-0.5 rounded-full",
                reachedLimit
                  ? "bg-red-100 text-red-700"
                  : turnCount >= turnLimit - 2
                    ? "bg-brand-gold/20 text-brand-gold"
                    : "bg-brand-gray/15 text-brand-dark/55",
              )}
              title={`已使用 ${turnCount} / ${turnLimit} 次對話`}
            >
              {turnCount}/{turnLimit}
            </span>
            {/* 手機收起按鈕（只 expanded 時顯示） */}
            {!minimized && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMinimize();
                }}
                aria-label="縮起對話視窗看更新"
                title="縮起來看更新"
                className="sm:hidden p-1 text-brand-dark/40 hover:text-brand-dark"
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label="關閉"
              className="p-1 text-brand-dark/40 hover:text-brand-dark"
            >
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-3 space-y-3 min-h-[200px]"
      >
        {messages.length === 0 && enabled && (
          <div className="text-xs text-brand-dark/50 italic px-2 py-3 leading-relaxed">
            {mode === "qa" ? (
              <>
                可以這樣問：
                <br />・「業主提的時程跟攝影團隊的拍攝排程有什麼風險？」
                <br />・「為什麼建議方向第二點優先級最高？」
                <br />・「下次提案要怎麼回應業主對預算的疑慮？」
              </>
            ) : (
              <>
                可以這樣說：
                <br />・「建議方向第二點請改成⋯⋯」
                <br />・「時程裡 5/4 那項換成下週三」
                <br />・「概要太長，縮短到三句話」
              </>
            )}
          </div>
        )}
        {!enabled && (
          <div className="text-xs text-brand-dark/40 italic px-2 py-3">
            等處理完成後可以在這裡修改
          </div>
        )}
        {messages.map((m) => (
          <ChatBubble
            key={m.id}
            message={m}
            showQaTag={m.role === "assistant" && m.id === lastQaAssistantId}
          />
        ))}
        {refining && <RefiningBubble />}
      </div>

      {/* 上限警示條 */}
      {reachedLimit && (
        <div className="px-3 py-2 border-t border-brand-gold/30 bg-brand-gold/10 text-[11px] text-brand-dark/80 leading-relaxed flex items-start gap-2">
          <svg
            className="w-3.5 h-3.5 mt-0.5 text-brand-gold flex-shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4M12 16h.01" />
          </svg>
          <div className="flex-1">
            這個微調視窗已經有
            <span className="num font-medium">{turnCount}</span>
            次對話，需要清理才能繼續。
            <button
              onClick={onAskClear}
              className="ml-1 underline text-brand-dark hover:text-brand-gold font-medium"
            >
              處理
            </button>
          </div>
        </div>
      )}

      <div
        className="border-t border-brand-gray/15 p-3 rounded-b-lg"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)" }}
      >
        {/* 模式切換：微調 / 提問 */}
        <div
          className="mb-2 flex items-center gap-1 bg-brand-cream/60 rounded-full p-0.5 w-fit"
          onTouchStart={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => setMode("refine")}
            className={cn(
              "text-[11px] px-2.5 py-1 rounded-full transition-colors whitespace-nowrap",
              mode === "refine"
                ? "bg-brand-dark text-brand-cream"
                : "text-brand-dark/55 hover:text-brand-dark",
            )}
          >
            微調
          </button>
          <button
            type="button"
            onClick={() => setMode("qa")}
            className={cn(
              "text-[11px] px-2.5 py-1 rounded-full transition-colors whitespace-nowrap",
              mode === "qa"
                ? "bg-brand-gold text-white"
                : "text-brand-dark/55 hover:text-brand-dark",
            )}
          >
            提問
          </button>
          <span className="text-[10px] text-brand-dark/45 ml-2 mr-2 leading-tight hidden sm:inline">
            {mode === "refine" ? "改既有內容" : "問題與答案會加進左邊「對話新增」"}
          </span>
        </div>
        <textarea
          rows={2}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            !enabled
              ? "等處理完成才能修改"
              : reachedLimit
                ? "已達對話上限，請先清理"
                : mode === "qa"
                  ? "想問什麼，這個會議的內容你都可以問"
                  : "告訴我要怎麼修改"
          }
          disabled={!enabled || refining || reachedLimit}
          className="w-full text-sm p-2 border border-brand-gray/30 rounded-md focus:outline-none focus:border-brand-gold disabled:bg-brand-gray/10 disabled:text-brand-gray font-sans"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              onSend();
            }
          }}
        />
        <div className="flex justify-between items-center gap-2 mt-2">
          <span className="text-[10px] text-brand-dark/40 hidden sm:inline">
            ⌘/Ctrl + Enter {mode === "qa" ? "問問看" : "送出"}
          </span>
          <button
            onClick={onMinimize}
            type="button"
            className="sm:hidden text-[11px] px-2.5 py-1.5 text-brand-dark/60 hover:text-brand-dark border border-brand-gray/30 rounded-md whitespace-nowrap"
          >
            縮起來看更新
          </button>
          <button
            onClick={onSend}
            disabled={!enabled || refining || !input.trim() || reachedLimit}
            className={cn(
              "text-xs px-3 py-1.5 rounded-md disabled:bg-brand-gray/30 disabled:text-brand-gray ml-auto sm:ml-0",
              mode === "qa"
                ? "bg-brand-gold text-white"
                : "bg-brand-dark text-brand-cream",
            )}
          >
            {mode === "qa" ? "問問看" : "送出"}
          </button>
        </div>
      </div>
    </div>
  );
}

// 單則對話氣泡：含複製按鈕
// 即時微調回應中的進度泡泡：計時 + 階段輪播文字
// 不接後端 job，純前端模擬，至少讓使用者知道沒有當機
function RefiningBubble() {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const PHASES: Array<{ from: number; label: string }> = [
    { from: 0, label: "讀取原始素材⋯⋯" },
    { from: 3, label: "比對 PDF 與圖片內容⋯⋯" },
    { from: 8, label: "Claude 思考中（這一段最久）⋯⋯" },
    { from: 25, label: "重新組織章節結構⋯⋯" },
    { from: 40, label: "套用排版樣式⋯⋯" },
    { from: 55, label: "差不多快好了⋯⋯" },
  ];
  let phase = PHASES[0].label;
  for (const p of PHASES) {
    if (elapsed >= p.from) phase = p.label;
  }

  // 跑馬條：用 indeterminate 風格（永遠在動）
  return (
    <div className="mr-6 bg-brand-gray/10 px-3 py-2.5 rounded-lg text-sm text-brand-dark/65 leading-relaxed">
      <div className="flex items-center gap-2">
        <span className="inline-flex gap-0.5 items-end" aria-hidden="true">
          <span className="w-1 h-1 bg-brand-gold rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-1 h-1 bg-brand-gold rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-1 h-1 bg-brand-gold rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
        </span>
        <span className="text-xs font-medium">{phase}</span>
        <span className="ml-auto num text-[10px] text-brand-dark/45">
          {elapsed}s
        </span>
      </div>
      <div className="mt-2 h-0.5 bg-brand-gray/15 rounded overflow-hidden">
        <div
          className="h-full bg-brand-gold/70 rounded animate-[refine-bar_2.2s_ease-in-out_infinite]"
          style={{ width: "40%" }}
        />
      </div>
      <style>{`
        @keyframes refine-bar {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(120%); }
          100% { transform: translateX(120%); }
        }
      `}</style>
    </div>
  );
}

function ChatBubble({
  message,
  showQaTag = false,
}: {
  message: MeetingMessage;
  // 該則助理訊息是否剛被「提問」模式寫進「對話新增」章節，加底部標記
  showQaTag?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const isUser = message.role === "user";

  const tokensInline = useMemo(() => {
    if (isUser) return null;
    const i = message.tokens_input ?? 0;
    const o = message.tokens_output ?? 0;
    if (i + o <= 0) return null;
    return `${formatTokens(i)} 輸入 / ${formatTokens(o)} 輸出 tokens`;
  }, [isUser, message.tokens_input, message.tokens_output]);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(message.content);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = message.content;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [message.content]);

  return (
    <div
      className={cn(
        "group relative px-3 py-2 pr-8 rounded-lg text-sm",
        isUser ? "ml-6 bg-brand-gold/10" : "mr-6 bg-brand-gray/10",
      )}
    >
      <div className="text-[10px] text-brand-dark/40 mb-1 uppercase tracking-wider">
        {isUser ? "你" : "ChillMeet"}
      </div>
      <div className="whitespace-pre-wrap leading-relaxed">
        {message.content}
      </div>
      {showQaTag && (
        <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-medium text-brand-gold border border-brand-gold/40 bg-brand-gold/10 rounded-full px-2 py-0.5">
          <svg
            className="w-3 h-3"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
          <span>已寫進左邊「對話新增」章節</span>
        </div>
      )}
      {tokensInline && (
        <div className="mt-1.5 text-[10px] text-brand-dark/40 num">
          {tokensInline}
        </div>
      )}
      <button
        onClick={copy}
        title="複製這則訊息"
        aria-label="複製這則訊息"
        className={cn(
          "absolute top-1.5 right-1.5 p-1 rounded transition-all",
          "text-brand-dark/35 hover:text-brand-dark hover:bg-white/70",
          "opacity-60 sm:opacity-0 sm:group-hover:opacity-100",
        )}
      >
        {copied ? (
          <svg
            className="w-3.5 h-3.5 text-brand-green"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
        ) : (
          <svg
            className="w-3.5 h-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
        )}
      </button>
      {copied && (
        <div className="absolute -top-6 right-1 text-[10px] bg-brand-dark text-white px-2 py-0.5 rounded shadow">
          已複製
        </div>
      )}
    </div>
  );
}

// 達上限後跳的清理確認 modal：先備份再清空
function ChatLimitModal({
  turnCount,
  turnLimit,
  messageCount,
  onBackup,
  onClear,
  onClose,
  clearing,
}: {
  turnCount: number;
  turnLimit: number;
  messageCount: number;
  onBackup: () => void;
  onClear: () => void;
  onClose: () => void;
  clearing: boolean;
}) {
  const [backedUp, setBackedUp] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);

  function handleBackup() {
    onBackup();
    setBackedUp(true);
  }

  function handleClearClick() {
    if (!backedUp && !confirmingClear) {
      // 還沒備份就要清，再多一次確認
      setConfirmingClear(true);
      return;
    }
    onClear();
  }

  return (
    <div className="fixed inset-0 z-50 bg-brand-dark/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden">
        <div className="px-5 py-4 border-b border-brand-gray/15 bg-brand-cream/40">
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-brand-gold"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
            <h3 className="text-sm font-medium text-brand-dark">
              對話視窗已達上限
            </h3>
          </div>
        </div>

        <div className="px-5 py-4 text-sm text-brand-dark/80 leading-relaxed space-y-3">
          <p>
            這個微調視窗已經和 ChillMeet 助理進行了
            <span className="num font-medium mx-1">{turnCount}</span>
            次對話（共 <span className="num">{messageCount}</span> 則訊息），達到{" "}
            <span className="num">{turnLimit}</span>{" "}
            次的上限。要繼續微調，需要先清理這個對話視窗。
          </p>
          <p className="text-xs text-brand-dark/60">
            清理「不會」影響左側的會議紀錄內容，只會清空這個對話視窗的歷史訊息。
            為了不弄丟對話過程，建議先下載一份備份。
          </p>
        </div>

        <div className="px-5 py-4 border-t border-brand-gray/15 space-y-2">
          <button
            onClick={handleBackup}
            className={cn(
              "w-full text-sm px-4 py-2.5 rounded-md border transition-colors flex items-center justify-center gap-2",
              backedUp
                ? "border-brand-green text-brand-green bg-brand-green/5"
                : "border-brand-dark text-brand-dark hover:bg-brand-cream",
            )}
          >
            <svg
              className="w-3.5 h-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {backedUp ? (
                <path d="M20 6L9 17l-5-5" />
              ) : (
                <>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </>
              )}
            </svg>
            {backedUp ? "已下載備份（再下載一次）" : "下載對話備份（.txt）"}
          </button>
          <button
            onClick={handleClearClick}
            disabled={clearing}
            className={cn(
              "w-full text-sm px-4 py-2.5 rounded-md transition-colors disabled:opacity-60",
              confirmingClear
                ? "bg-red-700 text-white hover:bg-red-800"
                : "bg-brand-dark text-brand-cream hover:bg-brand-gold",
            )}
          >
            {clearing
              ? "清理中⋯"
              : confirmingClear
                ? "尚未備份，確定要直接清空？"
                : backedUp
                  ? "清空對話視窗"
                  : "直接清空對話視窗（不備份）"}
          </button>
          <button
            onClick={onClose}
            disabled={clearing}
            className="w-full text-xs px-4 py-2 text-brand-dark/55 hover:text-brand-dark"
          >
            稍後再處理
          </button>
        </div>
      </div>
    </div>
  );
}

// 自動量測高度的 iframe — 讓會議紀錄以單頁方式呈現，外層瀏覽器接管捲軸
function AutoHeightIframe({
  srcDoc,
  title,
  iframeRef: externalRef,
}: {
  srcDoc: string;
  title: string;
  iframeRef?: React.RefObject<HTMLIFrameElement | null>;
}) {
  const localRef = useRef<HTMLIFrameElement>(null);
  const iframeRef = externalRef ?? localRef;
  const observerRef = useRef<ResizeObserver | null>(null);
  const [height, setHeight] = useState(800);

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  function handleLoad() {
    observerRef.current?.disconnect();

    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (!doc?.documentElement) return;

    const measure = () => {
      // 用 documentElement.scrollHeight 含 margin 較準
      const next = Math.max(
        doc.documentElement.scrollHeight,
        doc.body?.scrollHeight ?? 0,
      );
      if (next > 0) setHeight(next);
    };

    measure();

    const ro = new ResizeObserver(() => {
      // requestAnimationFrame 避免測量過於頻繁
      requestAnimationFrame(measure);
    });
    if (doc.body) ro.observe(doc.body);
    ro.observe(doc.documentElement);
    observerRef.current = ro;

    // 字體 / 圖片載入後再量一次
    doc.fonts?.ready?.then(measure).catch(() => {});
    doc.querySelectorAll("img").forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", measure, { once: true });
        img.addEventListener("error", measure, { once: true });
      }
    });
  }

  return (
    <iframe
      ref={iframeRef}
      srcDoc={srcDoc}
      title={title}
      className="w-full bg-white border-0 rounded-lg shadow-sm block"
      style={{ height, minHeight: 400 }}
      scrolling="no"
      onLoad={handleLoad}
    />
  );
}

// ===== 逐字稿編輯面板 =====

function TranscriptPanel({
  meetingId,
  open,
  onToggle,
  onRegenerate,
  regenerating,
  canRegenerate,
}: {
  meetingId: string;
  open: boolean;
  onToggle: () => void;
  onRegenerate: () => void | Promise<void>;
  regenerating: boolean;
  canRegenerate: boolean;
}) {
  const [transcript, setTranscript] = useState<string | null>(null);
  const [original, setOriginal] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!open || transcript !== null) return;
    setLoading(true);
    api
      .getTranscript(meetingId)
      .then((r) => {
        setTranscript(r.transcript);
        setOriginal(r.transcript);
      })
      .catch(() => {
        setTranscript("");
        setOriginal("");
      })
      .finally(() => setLoading(false));
  }, [open, meetingId, transcript]);

  const dirty = transcript !== null && transcript !== original;

  async function save() {
    if (!dirty || transcript === null) return;
    setSaving(true);
    try {
      await api.updateTranscript(meetingId, transcript);
      setOriginal(transcript);
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  }

  async function saveAndRegenerate() {
    await save();
    if (canRegenerate && !regenerating) {
      void onRegenerate();
    }
  }

  return (
    <div className="border-b border-brand-gray/15 bg-white/80 backdrop-blur">
      <div className="max-w-5xl mx-auto px-6">
        <button
          onClick={onToggle}
          className="w-full py-2.5 flex items-center justify-between text-left text-sm text-brand-dark/70 hover:text-brand-dark transition-colors"
        >
          <span className="flex items-center gap-2">
            <svg
              className={cn(
                "w-3.5 h-3.5 transition-transform",
                open && "rotate-90",
              )}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
            <span className="font-medium">逐字稿（PLAUD 轉錄結果，可編輯）</span>
            {transcript !== null && (
              <span className="text-xs text-brand-dark/45 num">
                （{transcript.length} 字）
              </span>
            )}
          </span>
          {dirty && !saving && (
            <span className="text-[11px] text-brand-gold">未儲存變更</span>
          )}
        </button>

        {open && (
          <div className="pb-4 space-y-2">
            <div className="text-xs text-brand-dark/55 leading-relaxed mb-2">
              直接修改錯字、人名、專有名詞。儲存後可一鍵重新整理會議紀錄。
              <span className="text-brand-dark/40">
                若要批次替換多筆相同錯字，建議用上方「修錯字」按鈕。
              </span>
            </div>
            {loading ? (
              <div className="text-xs text-brand-dark/40 italic py-3">載入中⋯⋯</div>
            ) : (
              <textarea
                value={transcript ?? ""}
                onChange={(e) => setTranscript(e.target.value)}
                rows={14}
                placeholder="（這場會議沒有逐字稿）"
                className="w-full text-sm px-3 py-2 border border-brand-gray/30 rounded-md focus:outline-none focus:border-brand-gold bg-white font-sans leading-relaxed"
                style={{ minHeight: 200 }}
              />
            )}
            <div className="flex gap-2 flex-wrap items-center">
              <button
                onClick={save}
                disabled={!dirty || saving}
                className="text-xs px-3 py-1.5 bg-brand-dark text-brand-cream rounded-md hover:bg-brand-gold disabled:opacity-40"
              >
                {saving ? "儲存中⋯" : "儲存逐字稿"}
              </button>
              <button
                onClick={saveAndRegenerate}
                disabled={!dirty || saving || regenerating || !canRegenerate}
                className="text-xs px-3 py-1.5 bg-brand-gold text-white rounded-md hover:bg-brand-gold/90 disabled:opacity-40"
              >
                {regenerating ? "重新整理中⋯" : "儲存並重新整理"}
              </button>
              {savedAt && !dirty && (
                <span className="text-[11px] text-brand-green">已儲存</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ===== 修錯字 modal（批次字串替換）=====

function ReplaceModal({
  meetingId,
  onClose,
  onDone,
}: {
  meetingId: string;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [rules, setRules] = useState<ReplaceRule[]>([
    { from: "", to: "", case_sensitive: false },
  ]);
  const [scope, setScope] = useState<"both" | "summary" | "transcript">("both");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<{
    transcript: number;
    summary: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  function updateRule(i: number, patch: Partial<ReplaceRule>) {
    setRules((rs) => rs.map((r, j) => (i === j ? { ...r, ...patch } : r)));
  }
  function addRule() {
    setRules((rs) => [...rs, { from: "", to: "", case_sensitive: false }]);
  }
  function removeRule(i: number) {
    setRules((rs) => rs.filter((_, j) => j !== i));
  }

  async function run() {
    const valid = rules.filter((r) => r.from.trim());
    if (valid.length === 0) {
      setError("至少要填一筆「找：」");
      return;
    }
    setError(null);
    setRunning(true);
    try {
      const r = await api.batchReplace(meetingId, valid, scope);
      setResult(r.counts);
      // 替換完不立刻關閉，讓使用者看結果，再按完成
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRunning(false);
    }
  }

  async function finish() {
    await onDone();
  }

  return (
    <div className="fixed inset-0 z-50 bg-brand-dark/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-xl w-full overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-5 py-4 border-b border-brand-gray/15 bg-brand-cream/40">
          <h3 className="text-sm font-medium text-brand-dark">修錯字（批次替換）</h3>
          <div className="text-[11px] text-brand-dark/55 mt-1">
            一次替換所有相同的錯字（人名、品牌、專有名詞）。預設不分大小寫，純文字替換、不耗 token、執行極快。
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <div className="flex gap-2 text-xs">
            <span className="text-brand-dark/55 self-center">套用範圍：</span>
            {(["both", "summary", "transcript"] as const).map((s) => (
              <button
                key={s}
                onClick={() => setScope(s)}
                className={cn(
                  "px-2.5 py-1 rounded-full border",
                  scope === s
                    ? "bg-brand-dark text-brand-cream border-brand-dark"
                    : "border-brand-gray/30 text-brand-dark/70 hover:border-brand-dark",
                )}
              >
                {s === "both" ? "整理結果＋逐字稿" : s === "summary" ? "只動整理結果" : "只動逐字稿"}
              </button>
            ))}
          </div>

          {rules.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 items-center">
              <input
                type="text"
                placeholder="找（錯誤的字）"
                value={r.from}
                onChange={(e) => updateRule(i, { from: e.target.value })}
                className="text-sm px-2.5 py-1.5 border border-brand-gray/30 rounded-md focus:outline-none focus:border-brand-gold bg-white"
              />
              <span className="text-brand-dark/40 text-sm">→</span>
              <input
                type="text"
                placeholder="替換為（正確的字）"
                value={r.to}
                onChange={(e) => updateRule(i, { to: e.target.value })}
                className="text-sm px-2.5 py-1.5 border border-brand-gray/30 rounded-md focus:outline-none focus:border-brand-gold bg-white"
              />
              <button
                onClick={() => removeRule(i)}
                disabled={rules.length === 1}
                className="text-xs text-brand-dark/40 hover:text-red-700 disabled:opacity-30 px-2"
              >
                移除
              </button>
            </div>
          ))}

          <button
            onClick={addRule}
            className="text-xs text-brand-gold hover:text-brand-dark"
          >
            + 加一筆替換規則
          </button>

          {result && (
            <div className="text-xs bg-brand-cream/60 border border-brand-gold/30 rounded-md p-3 space-y-1">
              <div className="font-medium text-brand-dark">
                替換完成：
              </div>
              <div className="text-brand-dark/70">
                整理結果改了 <span className="num font-medium">{result.summary}</span> 處 ·
                逐字稿改了 <span className="num font-medium">{result.transcript}</span> 處
              </div>
            </div>
          )}

          {error && <div className="text-xs text-red-700">{error}</div>}
        </div>

        <div className="px-5 py-4 border-t border-brand-gray/15 flex justify-end gap-2 bg-brand-cream/40">
          <button
            onClick={onClose}
            disabled={running}
            className="text-xs px-3 py-2 text-brand-dark/60 hover:text-brand-dark"
          >
            {result ? "稍後再說" : "取消"}
          </button>
          {!result ? (
            <button
              onClick={run}
              disabled={running}
              className="text-xs px-4 py-2 bg-brand-dark text-brand-cream rounded-md hover:bg-brand-gold disabled:opacity-50"
            >
              {running ? "替換中⋯" : "執行替換"}
            </button>
          ) : (
            <button
              onClick={finish}
              className="text-xs px-4 py-2 bg-brand-gold text-white rounded-md hover:bg-brand-dark"
            >
              完成（重新載入頁面）
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== 附帶頁面（會前／會後另外整理的網頁，紀錄頁只放連結）=====

function AttachmentsPanel({
  meetingId,
  attachments,
  onChanged,
}: {
  meetingId: string;
  attachments: MeetingAttachment[];
  onChanged: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [when, setWhen] = useState<"before" | "after">("before");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function save(next: MeetingAttachment[], doneMsg: string) {
    setSaving(true);
    setMsg(null);
    try {
      await api.setAttachments(meetingId, next);
      await onChanged();
      setMsg(doneMsg);
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function add() {
    const u = url.trim();
    if (!/^https?:\/\//i.test(u)) {
      setMsg("網址要用 https:// 開頭");
      return;
    }
    await save([...attachments, { title: title.trim(), url: u, when }], "已加入，紀錄頁上方會出現連結");
    setTitle("");
    setUrl("");
  }

  return (
    <div className="border-b border-brand-gray/15 bg-white/80 backdrop-blur">
      <div className="max-w-5xl mx-auto px-6">
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-full py-2.5 flex items-center justify-between text-left text-sm text-brand-dark/70 hover:text-brand-dark transition-colors"
        >
          <span className="flex items-center gap-2">
            <svg
              className={cn("w-3.5 h-3.5 transition-transform", open && "rotate-90")}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
            <span className="font-medium">附帶頁面</span>
            <span className="text-xs text-brand-dark/45 num">（{attachments.length} 個）</span>
          </span>
          <span className="text-xs text-brand-dark/45 hidden sm:inline">會前／會後另外整理的網頁</span>
        </button>
        {open && (
          <div className="pb-4 space-y-3">
            {attachments.length > 0 && (
              <ul className="divide-y divide-brand-gray/15 border-y border-brand-gray/15">
                {attachments.map((a, i) => (
                  <li key={i} className="flex items-center gap-3 py-2 min-w-0">
                    <span className="text-xs font-bold text-brand-gold border border-brand-gold px-1.5 shrink-0">
                      {a.when === "after" ? "會後" : "會前"}
                    </span>
                    <a
                      href={a.url}
                      target="_blank"
                      rel="noopener"
                      className="text-sm text-brand-dark min-w-0 flex-1 truncate hover:underline"
                      title={a.url}
                    >
                      {a.title || a.url}
                    </a>
                    <button
                      onClick={() => save(attachments.filter((_, j) => j !== i), "已移除")}
                      disabled={saving}
                      className="text-xs text-brand-dark/50 hover:text-red-700 shrink-0"
                    >
                      移除
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-col sm:flex-row gap-2 min-w-0">
              <select
                value={when}
                onChange={(e) => setWhen(e.target.value as "before" | "after")}
                className="text-sm px-2 py-1.5 border border-brand-gray/30 rounded-md bg-white shrink-0"
                aria-label="會前或會後"
              >
                <option value="before">會前</option>
                <option value="after">會後</option>
              </select>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="名稱，例：腳本路線圖"
                className="text-sm px-2.5 py-1.5 border border-brand-gray/30 rounded-md bg-white min-w-0 sm:w-56"
              />
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://"
                inputMode="url"
                className="text-sm px-2.5 py-1.5 border border-brand-gray/30 rounded-md bg-white min-w-0 flex-1"
              />
              <button
                onClick={add}
                disabled={saving || !url.trim()}
                className="text-sm px-4 py-1.5 rounded-md bg-brand-dark text-white disabled:opacity-40 shrink-0"
              >
                {saving ? "儲存中…" : "加入"}
              </button>
            </div>
            {msg && <p className="text-xs text-brand-dark/60">{msg}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

// ===== 分享 modal（建立短網址 + 勾選章節）=====

const SECTION_OPTIONS: Array<{ key: SectionKey; label: string; desc: string }> = [
  { key: "summary", label: "結論與概要", desc: "一句話結論＋兩三句概要" },
  { key: "decisions", label: "定了的事", desc: "雙方已經講定的結論" },
  { key: "todos", label: "要做的事", desc: "誰、什麼時候、做什麼" },
  { key: "open", label: "還沒定的事", desc: "等誰回覆、卡住什麼" },
  { key: "attachments", label: "附帶頁面", desc: "會前／會後另外整理的網頁連結" },
  { key: "detailed", label: "會議內容", desc: "三疊以外的討論細節" },
  { key: "references", label: "業主提供的資料", desc: "PDF / 圖片 / 連結" },
  { key: "agenda", label: "會前清單對照", desc: "會前要問的事答到哪些（內部用）" },
  { key: "dialog", label: "對話新增", desc: "對話視窗提問累積的問答" },
  { key: "recommendations", label: "我們的建議", desc: "整理者的判斷，不是會議結論" },
];
// 「我們的建議」「會前清單」「附帶頁面」「對話新增」預設不勾（用戶 2026-10-20 定）
const DEFAULT_SHARE_SECTIONS: SectionKey[] = ["summary", "decisions", "todos", "open", "detailed", "references"];

function ShareModal({
  meetingId,
  onClose,
}: {
  meetingId: string;
  onClose: () => void;
}) {
  const [sections, setSections] = useState<Set<SectionKey>>(
    new Set(DEFAULT_SHARE_SECTIONS),
  );
  const [expiresInDays, setExpiresInDays] = useState<number | null>(30);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shares, setShares] = useState<ShareLink[]>([]);
  const [newUrl, setNewUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api
      .listShares(meetingId)
      .then((r) => setShares(r.shares))
      .catch(() => {});
  }, [meetingId]);

  function toggle(k: SectionKey) {
    setSections((s) => {
      const next = new Set(s);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }

  async function create() {
    if (sections.size === 0) {
      setError("至少要勾一個章節");
      return;
    }
    setError(null);
    setCreating(true);
    try {
      const r = await api.createShare(meetingId, {
        sections: Array.from(sections),
        expires_in_days: expiresInDays,
      });
      setNewUrl(r.url);
      const list = await api.listShares(meetingId);
      setShares(list.shares);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCreating(false);
    }
  }

  async function copyUrl() {
    if (!newUrl) return;
    try {
      await navigator.clipboard.writeText(newUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // 失敗就算了
    }
  }

  // 既有列表：複製單筆短連結
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  async function copyShareCode(code: string) {
    const url = `${window.location.origin}/s/${code}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // fallback：用 textarea + execCommand
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        // 真的複製不到就算了
      }
      document.body.removeChild(ta);
    }
    setCopiedCode(code);
    setTimeout(() => setCopiedCode((c) => (c === code ? null : c)), 1800);
  }

  async function revoke(code: string) {
    if (!confirm("確定撤銷這個分享連結？")) return;
    await api.deleteShare(meetingId, code);
    const list = await api.listShares(meetingId);
    setShares(list.shares);
  }

  return (
    <div className="fixed inset-0 z-50 bg-brand-dark/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-xl w-full overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-5 py-4 border-b border-brand-gray/15 bg-brand-cream/40">
          <h3 className="text-sm font-medium text-brand-dark">分享給業主（短網址）</h3>
          <div className="text-[11px] text-brand-dark/55 mt-1">
            勾選想分享的章節，按下「建立連結」後直接傳網址給業主即可。原始會議紀錄不受影響。
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {newUrl ? (
            <div className="bg-brand-gold/10 border border-brand-gold/30 rounded-md p-4 space-y-2">
              <div className="text-xs text-brand-dark/60">分享連結已建立：</div>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={newUrl}
                  className="flex-1 text-sm px-2 py-1.5 border border-brand-gray/30 rounded bg-white num"
                />
                <button
                  onClick={copyUrl}
                  className="text-xs px-3 py-1.5 bg-brand-dark text-brand-cream rounded-md hover:bg-brand-gold"
                >
                  {copied ? "已複製" : "複製"}
                </button>
              </div>
              <div className="text-[11px] text-brand-dark/55">
                {expiresInDays === null
                  ? "永久有效"
                  : `將於 ${expiresInDays} 天後過期`}
                · 含章節：
                {Array.from(sections)
                  .map(
                    (k) =>
                      SECTION_OPTIONS.find((o) => o.key === k)?.label ?? k,
                  )
                  .join("、")}
              </div>
            </div>
          ) : (
            <>
              <div>
                <div className="text-xs text-brand-dark/55 mb-2">勾選要分享的章節：</div>
                <div className="space-y-1.5">
                  {SECTION_OPTIONS.map((o) => (
                    <label
                      key={o.key}
                      className="flex items-start gap-2 px-3 py-2 border border-brand-gray/20 rounded-md cursor-pointer hover:bg-brand-cream/50"
                    >
                      <input
                        type="checkbox"
                        checked={sections.has(o.key)}
                        onChange={() => toggle(o.key)}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="text-sm text-brand-dark">{o.label}</div>
                        <div className="text-[11px] text-brand-dark/50">{o.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs text-brand-dark/55 mb-2">過期時間：</div>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { v: 7, label: "7 天" },
                    { v: 30, label: "30 天" },
                    { v: 90, label: "90 天" },
                    { v: null, label: "永久" },
                  ].map((opt) => (
                    <button
                      key={String(opt.v)}
                      onClick={() => setExpiresInDays(opt.v)}
                      className={cn(
                        "text-xs px-3 py-1.5 rounded-full border",
                        expiresInDays === opt.v
                          ? "bg-brand-dark text-brand-cream border-brand-dark"
                          : "border-brand-gray/30 text-brand-dark/70 hover:border-brand-dark",
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* 既有的分享連結列表 */}
          {shares.length > 0 && (
            <div className="border-t border-brand-gray/15 pt-4">
              <div className="text-xs text-brand-dark/55 mb-2">
                這場會議的分享紀錄（{shares.length}）
              </div>
              <div className="space-y-1.5">
                {shares.map((s) => {
                  const expired =
                    s.expires_at !== null &&
                    s.expires_at < Math.floor(Date.now() / 1000);
                  return (
                    <div
                      key={s.code}
                      className="flex items-center gap-2 text-xs border border-brand-gray/15 rounded-md px-3 py-2"
                    >
                      <span className="num font-medium text-brand-dark">
                        /s/{s.code}
                      </span>
                      <span className="text-brand-dark/45">·</span>
                      <span className="text-brand-dark/55">
                        {s.view_count} 次查看
                      </span>
                      <span className="text-brand-dark/45">·</span>
                      <span
                        className={cn(
                          "text-[10px]",
                          expired ? "text-red-700" : "text-brand-dark/55",
                        )}
                      >
                        {s.expires_at === null
                          ? "永久"
                          : expired
                            ? "已過期"
                            : `${Math.ceil((s.expires_at - Date.now() / 1000) / 86400)} 天後過期`}
                      </span>
                      <span className="flex-1" />
                      <button
                        onClick={() => copyShareCode(s.code)}
                        className={cn(
                          "px-2 py-0.5 rounded transition-colors",
                          copiedCode === s.code
                            ? "bg-brand-gold/20 text-brand-gold"
                            : "text-brand-dark/55 hover:text-brand-gold hover:bg-brand-cream",
                        )}
                        title="複製完整短網址到剪貼簿"
                      >
                        {copiedCode === s.code ? "已複製" : "複製連結"}
                      </button>
                      <button
                        onClick={() => window.open(`${window.location.origin}/s/${s.code}`, "_blank", "noopener")}
                        className="px-2 py-0.5 rounded text-brand-dark/55 hover:text-brand-dark hover:bg-brand-cream"
                        title="開新分頁預覽"
                      >
                        開啟
                      </button>
                      <button
                        onClick={() => revoke(s.code)}
                        className="text-brand-dark/40 hover:text-red-700"
                      >
                        撤銷
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {error && <div className="text-xs text-red-700">{error}</div>}
        </div>

        <div className="px-5 py-4 border-t border-brand-gray/15 flex justify-end gap-2 bg-brand-cream/40">
          <button
            onClick={onClose}
            className="text-xs px-3 py-2 text-brand-dark/60 hover:text-brand-dark"
          >
            關閉
          </button>
          {!newUrl && (
            <button
              onClick={create}
              disabled={creating || sections.size === 0}
              className="text-xs px-4 py-2 bg-brand-gold text-white rounded-md hover:bg-brand-dark disabled:opacity-50"
            >
              {creating ? "建立中⋯" : "建立分享連結"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== 真實進度面板（取代假秒數）=====

function RegenerateProgress({
  job,
  smoothPct,
  startedAt,
}: {
  job: Job | null;
  smoothPct: number;
  startedAt: number;
}) {
  // 倒數秒數：若已知進度，依比例反推剩餘；否則用固定 25s 估算
  const elapsed = startedAt > 0 ? Math.floor((Date.now() - startedAt) / 1000) : 0;
  const ESTIMATED_TOTAL = 28; // 秒，根據實際 PDF 分析經驗值
  const pct = Math.max(smoothPct, job?.progress_percent ?? 0);
  const remaining = pct > 0 && pct < 100
    ? Math.max(2, Math.round((100 - pct) / Math.max(pct, 5) * elapsed))
    : Math.max(2, ESTIMATED_TOTAL - elapsed);

  // 解析 log_messages（最後 5 條）
  const logs = useMemo(() => {
    if (!job?.log_messages) return [];
    try {
      const all = JSON.parse(job.log_messages) as Array<{
        timestamp: number;
        level: string;
        message: string;
      }>;
      return all.slice(-5).reverse(); // 最新在上
    } catch {
      return [];
    }
  }, [job?.log_messages]);

  const stepLabel = job?.step_label ?? "啟動處理⋯⋯";

  return (
    <div className="bg-white rounded-lg border border-brand-gray/15 px-6 py-8 sm:px-10 sm:py-10 my-4 mx-2 sm:mx-0 shadow-sm">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-5 h-5 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
        <span className="text-sm font-medium text-brand-dark">{stepLabel}</span>
        <span className="ml-auto text-xs text-brand-dark/55 num">
          {Math.round(pct)}%
        </span>
      </div>

      {/* 進度條 */}
      <div className="h-2 bg-brand-gray/15 rounded-full overflow-hidden mb-2">
        <div
          className="h-full bg-gradient-to-r from-brand-gold to-brand-dark rounded-full transition-all duration-500"
          style={{ width: `${Math.max(2, Math.min(100, pct))}%` }}
        />
      </div>

      <div className="flex justify-between text-[11px] text-brand-dark/50 num mb-6">
        <span>已花 {elapsed} 秒</span>
        <span>剩餘約 {remaining} 秒</span>
      </div>

      {/* 滾動式處理紀錄 */}
      {logs.length > 0 && (
        <div className="border-t border-brand-gray/10 pt-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-brand-dark/45 mb-2">
            處理紀錄
          </div>
          <div className="space-y-1.5 text-xs leading-relaxed">
            {logs.map((l, i) => (
              <div
                key={`${l.timestamp}-${i}`}
                className={cn(
                  "flex gap-2",
                  i === 0 ? "text-brand-dark" : "text-brand-dark/55",
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 w-1.5 h-1.5 rounded-full flex-shrink-0",
                    l.level === "error"
                      ? "bg-red-500"
                      : l.level === "warn"
                        ? "bg-yellow-500"
                        : l.level === "success"
                          ? "bg-brand-green"
                          : i === 0
                            ? "bg-brand-gold animate-pulse"
                            : "bg-brand-gray/40",
                  )}
                />
                <span className="flex-1 break-words">{l.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-[11px] text-brand-dark/40 mt-6 leading-relaxed">
        正在重新分析這場會議的素材（PLAUD 逐字稿、PDF、圖片、純文字）。<br />
        分頁可以保留不關，過程中切到其他分頁也沒關係。
      </div>
    </div>
  );
}

// ===== 回到頂端浮動按鈕 =====

function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!show) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      title="回到最上面"
      aria-label="回到最上面"
      className="fixed right-6 bottom-44 z-30 w-12 h-12 rounded-full bg-white border border-brand-gray/30 shadow-md hover:shadow-lg flex items-center justify-center text-brand-dark/70 hover:text-brand-dark hover:border-brand-dark transition-all"
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="19" x2="12" y2="5" />
        <polyline points="5 12 12 5 19 12" />
      </svg>
    </button>
  );
}

// ===== 右側目錄（TOC）：滾動時自動 highlight 當前章節 =====

const TOC_ITEMS: Array<{ key: SectionKey; label: string }> = [
  { key: "summary", label: "結論" },
  { key: "decisions", label: "定了的事" },
  { key: "todos", label: "要做的事" },
  { key: "open", label: "還沒定的事" },
  { key: "agenda", label: "會前清單" },
  { key: "detailed", label: "會議內容" },
  { key: "references", label: "業主資料" },
  { key: "dialog", label: "對話新增" },
  { key: "recommendations", label: "我們的建議" },
];

function RightSideToc({
  iframeRef,
  enabled,
}: {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  enabled: boolean;
}) {
  const [activeKey, setActiveKey] = useState<SectionKey | null>(null);
  const [availableKeys, setAvailableKeys] = useState<SectionKey[]>([]);

  useEffect(() => {
    if (!enabled) return;
    let raf = 0;
    let stop = false;

    const measure = () => {
      if (stop) return;
      const iframe = iframeRef.current;
      const doc = iframe?.contentDocument;
      if (!doc) {
        raf = requestAnimationFrame(measure);
        return;
      }
      const sections = Array.from(
        doc.querySelectorAll<HTMLElement>("[data-section-key]"),
      );
      if (sections.length === 0) {
        raf = requestAnimationFrame(measure);
        return;
      }
      // 收集可見 keys
      const keys = sections
        .map((s) => s.getAttribute("data-section-key") as SectionKey | null)
        .filter((k): k is SectionKey => !!k);
      setAvailableKeys((prev) => (prev.length === keys.length ? prev : keys));

      // 選距離 viewport 30% 處最近的 section 當 active
      const iframeRect = iframe!.getBoundingClientRect();
      const target = window.innerHeight * 0.3;
      let bestKey: SectionKey | null = null;
      let bestDist = Infinity;
      sections.forEach((s) => {
        const r = s.getBoundingClientRect();
        const inParentTop = iframeRect.top + r.top;
        const inParentBottom = inParentTop + r.height;
        if (inParentBottom > 0 && inParentTop < window.innerHeight) {
          const d = Math.abs(inParentTop - target);
          if (d < bestDist) {
            bestDist = d;
            bestKey = s.getAttribute("data-section-key") as SectionKey;
          }
        }
      });
      if (bestKey) setActiveKey(bestKey);
    };

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    measure();
    // 初始載入時 iframe 可能還沒 render 完，輪詢直到拿到 sections
    const interval = setInterval(measure, 800);
    setTimeout(() => clearInterval(interval), 5000);

    return () => {
      stop = true;
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      clearInterval(interval);
    };
  }, [enabled, iframeRef]);

  function scrollToSection(key: SectionKey) {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (!doc) return;
    const sec = doc.querySelector<HTMLElement>(`[data-section-key="${key}"]`);
    if (!sec) return;
    const iframeTop = iframe!.getBoundingClientRect().top + window.scrollY;
    const offset = sec.getBoundingClientRect().top;
    // sticky bar 的高度約 64px，預留 80px 空間
    window.scrollTo({ top: iframeTop + offset - 80, behavior: "smooth" });
  }

  if (!enabled || availableKeys.length === 0) return null;

  const items = TOC_ITEMS.filter((i) => availableKeys.includes(i.key));

  return (
    <nav
      className="hidden xl:block fixed top-44 z-20"
      style={{ right: "max(24px, calc((100vw - 1024px) / 2 - 200px))" }}
      aria-label="會議紀錄目錄"
    >
      <div className="text-xs text-brand-dark/40 mb-3">章節</div>
      <ul className="space-y-1.5">
        {items.map((item) => {
          const active = activeKey === item.key;
          return (
            <li key={item.key}>
              <button
                onClick={() => scrollToSection(item.key)}
                className={cn(
                  "block text-left text-xs leading-relaxed transition-all w-full pl-3 -ml-px border-l-2 whitespace-nowrap",
                  active
                    ? "text-brand-dark font-medium border-brand-gold scale-105"
                    : "text-brand-dark/45 hover:text-brand-dark border-brand-gray/30 hover:border-brand-dark/50",
                )}
              >
                {item.label}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

// ===== Status label =====

function StatusLabel({ status }: { status: Meeting["status"] }) {
  const map: Record<Meeting["status"], { label: string; color: string }> = {
    draft: { label: "草稿", color: "text-brand-dark/50" },
    queued: { label: "排隊中", color: "text-blue-700" },
    processing: { label: "處理中", color: "text-brand-gold" },
    awaiting_cli: { label: "等 Claude Code", color: "text-brand-dark/70" },
    completed: { label: "完成", color: "text-brand-green" },
    failed: { label: "失敗", color: "text-red-700" },
    archived: { label: "封存", color: "text-brand-gray" },
  };
  const v = map[status];
  return <span className={v.color}>{v.label}</span>;
}

// ===== 最近一次 AI 成本 toast =====
// refine 或 regenerate 後跳出，顯示真實 token + TWD（後端依公開報價即時計算）
function RecentCostToast({
  refine,
  regen,
  onDismiss,
}: {
  refine: ClaudeCostBreakdown | null;
  regen: JobResultCost | null;
  onDismiss: () => void;
}) {
  // 30 秒後自動隱藏
  useEffect(() => {
    if (!refine && !regen) return;
    const t = setTimeout(onDismiss, 30000);
    return () => clearTimeout(t);
  }, [refine, regen, onDismiss]);

  if (!refine && !regen) return null;

  const lines: { label: string; detail: string }[] = [];
  if (refine) {
    lines.push({
      label: shortModelName(refine.model),
      detail: `${formatTokens(refine.input_tokens)} 輸入 / ${formatTokens(refine.output_tokens)} 輸出 · 約 ${formatTwd(refine.twd)}`,
    });
  }
  if (regen) {
    if (regen.claude) {
      lines.push({
        label: shortModelName(regen.claude.model),
        detail: `${formatTokens(regen.claude.input_tokens)} 輸入 / ${formatTokens(regen.claude.output_tokens)} 輸出 · 約 ${formatTwd(regen.claude.twd)}`,
      });
    }
    if (regen.whisper) {
      lines.push({
        label: "Whisper",
        detail: `${regen.whisper.seconds} 秒 · 約 ${formatTwd(regen.whisper.twd)}`,
      });
    }
  }
  const totalTwd =
    (refine?.twd ?? 0) + (regen?.total_twd ?? 0);

  return (
    <div className="fixed bottom-6 left-6 z-40 max-w-xs bg-white border border-brand-gold/40 rounded-lg shadow-xl px-4 py-3">
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-brand-dark/50">
          本次消耗
        </div>
        <button
          onClick={onDismiss}
          className="text-[10px] text-brand-dark/40 hover:text-brand-dark"
          aria-label="關閉"
        >
          ✕
        </button>
      </div>
      <div className="text-2xl font-bold text-brand-dark num mb-2">
        {formatTwd(totalTwd)}
      </div>
      <div className="space-y-1">
        {lines.map((l, i) => (
          <div key={i} className="text-[11px] text-brand-dark/70 leading-tight">
            <span className="inline-block px-1.5 py-0.5 mr-1.5 rounded bg-brand-gold/15 text-brand-dark text-[9px] font-medium">
              {l.label}
            </span>
            <span className="break-words">{l.detail}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 text-[9px] text-brand-dark/40 leading-relaxed">
        以 Anthropic 公開報價即時計算，含匯率換算。
      </div>
    </div>
  );
}
